import { compareMoney, isZeroMoney, negateMoney } from '@familywallet/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, ChevronRight, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { reportsKey } from '../lib/ledger';
import { formatAmount } from '../lib/money';
import { cn } from '../lib/utils';
import { Card } from './ui/card';

/**
 * Home summary of the Reports screen (Phase 6 PR 2): spent so far this month against the same
 * days of last month, and where most of it went. Same API and visibility as Reports ("My
 * spending" without reports.view).
 */
export function ReportsHomeCard() {
  const { t, i18n } = useTranslation();
  const scope = {};
  const compare = useQuery({
    queryKey: [...reportsKey, 'compare', scope],
    queryFn: () => api.reports.compare(scope),
  });
  const month = compare.data?.month;
  const spending = useQuery({
    queryKey: [...reportsKey, 'spending', undefined, month],
    queryFn: () => api.reports.spending({ month }),
    enabled: month !== undefined,
  });
  if (!compare.data) return null;
  const { overall, currency } = compare.data;
  const money = (amount: string, signed = false) =>
    formatAmount(amount, currency, i18n.language, { signed });
  const direction = compareMoney(overall.difference, '0');
  const Icon = direction > 0 ? ArrowUpRight : direction < 0 ? ArrowDownRight : Minus;
  const top = spending.data?.categories[0];
  const nothing = isZeroMoney(overall.current) && isZeroMoney(overall.previous);
  return (
    <Link
      to="/reports"
      className="block rounded-3xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <Card className="flex flex-col gap-2 hover:bg-surface-2">
        <span className="flex items-center justify-between gap-3">
          <span className="text-[15px] font-semibold">{t('reports.home.title')}</span>
          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-accent">
            {t('reports.home.see')}
            <ChevronRight aria-hidden="true" className="size-4 rtl:rotate-180" />
          </span>
        </span>
        {nothing ? (
          <span className="text-[13px] text-muted">{t('reports.home.nothing')}</span>
        ) : (
          <>
            <span dir="ltr" className="text-2xl font-semibold tabular-nums text-start">
              {money(overall.current)}
            </span>
            <span
              className={cn(
                'inline-flex items-center gap-1 text-[13px]',
                direction > 0 ? 'text-expense' : direction < 0 ? 'text-accent' : 'text-muted',
              )}
            >
              <Icon aria-hidden="true" className="size-4 shrink-0" />
              {direction === 0
                ? t('reports.home.same')
                : t(direction > 0 ? 'reports.home.more' : 'reports.home.less', {
                    amount: money(
                      direction > 0 ? overall.difference : negateMoney(overall.difference),
                    ),
                    percent: overall.percent === null ? '' : ` (${overall.percent}%)`,
                  })}
            </span>
            {top && (
              <span className="text-[13px] text-muted">
                {t('reports.home.top', { name: top.name, amount: money(top.amount) })}
              </span>
            )}
          </>
        )}
      </Card>
    </Link>
  );
}
